import { Fragment, createElement } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { zerlegeMarkdown } from '@mcikm-vote/shared';
import type { RichBlock, Textteil, Ueberschriftsstufe } from '@mcikm-vote/shared';

/**
 * Anzeige eines Rechtstexts — Impressum und Datenschutz (Änderungsanforderung ÄA-5).
 *
 * **Hier entsteht kein HTML-String, und `dangerouslySetInnerHTML` kommt nicht vor.** Das
 * gespeicherte Markdown wird in einen typisierten Baum zerlegt und dieser als React-Elemente
 * ausgegeben — dieselbe Bauweise wie bei {@link FormatierterText} für Abstimmungstexte (§12).
 * Eingefügtes HTML hat im Baum keine Entsprechung und bleibt gewöhnlicher Text.
 *
 * Dieselbe Komponente zeigt den Text auf der öffentlichen Seite und in der Vorschau des
 * Verwaltungsbereichs. Nur so kann die Vorschau nicht von dem abweichen, was Besucher sehen.
 */
export function RichText({ markdown }: { markdown: string }): ReactElement {
  const bloecke = zerlegeMarkdown(markdown);

  return (
    <div className="flex flex-col gap-3">
      {bloecke.map((block, index) => (
        <Block key={index} block={block} />
      ))}
    </div>
  );
}

/** Grade der Überschriften. H1 bleibt der Seitenüberschrift gleich, die übrigen staffeln darunter. */
const UEBERSCHRIFT_KLASSE: Record<Ueberschriftsstufe, string> = {
  1: 'text-2xl font-semibold mt-2',
  2: 'text-xl font-semibold mt-2',
  3: 'text-lg font-semibold mt-1',
  4: 'text-base font-semibold',
  5: 'text-base font-medium',
  6: 'text-sm font-medium',
};

function Block({ block }: { block: RichBlock }): ReactElement {
  if (block.art === 'absatz') {
    // Ein Absatz kann mehrere Zeilen haben — in einem Impressum steht die Anschrift
    // untereinander. Zwischen den Zeilen ein `<br />`, hinter der letzten keines.
    return (
      <p>
        {block.zeilen.map((zeile, index) => (
          <Fragment key={index}>
            {index > 0 && <br />}
            {teile(zeile)}
          </Fragment>
        ))}
      </p>
    );
  }

  if (block.art === 'ueberschrift') {
    // `createElement` statt sechs Zweigen: Der Grad ist bereits auf 1 bis 6 eingegrenzt, ein
    // beliebiger Elementname kann hier nicht entstehen.
    return createElement(
      `h${block.stufe}`,
      { className: UEBERSCHRIFT_KLASSE[block.stufe] },
      teile(block.teile),
    );
  }

  const klassen = 'flex flex-col gap-1 pl-5';

  return block.nummeriert ? (
    <ol className={`${klassen} list-decimal`}>
      {block.punkte.map((punkt, index) => (
        <li key={index}>{teile(punkt)}</li>
      ))}
    </ol>
  ) : (
    <ul className={`${klassen} list-disc`}>
      {block.punkte.map((punkt, index) => (
        <li key={index}>{teile(punkt)}</li>
      ))}
    </ul>
  );
}

function teile(liste: readonly Textteil[]): ReactNode {
  return liste.map((teil, index) => <Teil key={index} teil={teil} />);
}

function Teil({ teil }: { teil: Textteil }): ReactElement {
  switch (teil.art) {
    case 'text':
      return <Fragment>{teil.text}</Fragment>;

    case 'fett':
      return <strong className="font-semibold">{teile(teil.teile)}</strong>;

    case 'kursiv':
      return <em className="italic">{teile(teil.teile)}</em>;

    case 'link': {
      // Das Ziel hat der Zerleger bereits auf http, https und mailto eingegrenzt.
      //
      // `target="_blank"` gehört nur an Webadressen. Bei `mailto:` öffneten manche Browser einen
      // leeren Tab neben dem Mailprogramm — und „in neuem Tab öffnen" ergibt für eine
      // E-Mail-Adresse ohnehin keinen Sinn.
      const extern = teil.ziel.toLowerCase().startsWith('http');

      return (
        <a
          href={teil.ziel}
          {...(extern ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
          className="text-akzent underline"
        >
          {teile(teil.teile)}
        </a>
      );
    }
  }
}
